import React, { useEffect, useState } from 'react';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, ButtonBar, Checkbox, Dialog } from '@ygtechnologies/rubus-ui';
import {
    addInheritedMember,
    editInheritedMember,
    getInheritedMemberProfiles,
    getInheritedMemberProjects,
    previewAddInheritedMember,
    InheritedMemberPreview,
    ProfileMembershipGrant,
    ProjectMembershipGrant,
} from '@/services/inheritedMembers';
import theme from './MakeWatcherConfirmModal.module.scss';

interface Props {
    isOpen: boolean;
    profileId: number;
    profileName: string;
    userId: number;
    userDisplayName: string;
    mode?: 'add' | 'edit';
    onClose(): void;
    onSuccess(): void;
}

type Role = 'Admin' | 'Member';

const MakeWatcherConfirmModal: React.FC<Props> = ({
    isOpen,
    profileId,
    profileName,
    userId,
    userDisplayName,
    mode = 'add',
    onClose,
    onSuccess,
}) => {
    const isEdit = mode === 'edit';
    const [addToProfiles, setAddToProfiles] = useState(true);
    const [profileRole, setProfileRole] = useState<Role>('Admin');

    const [addToProjects, setAddToProjects] = useState(true);
    const [projectRole, setProjectRole] = useState<Role>('Member');
    const [projectHasAnalytics, setProjectHasAnalytics] = useState(true);
    const [projectNotificationsEnabled, setProjectNotificationsEnabled] = useState(false);

    const [preview, setPreview] = useState<InheritedMemberPreview | null>(null);

    // Projects the admin unticked. Kept out of the preview request on purpose:
    // the backend would sort an excluded project into `skipped`, its checkbox
    // would vanish on the refetch and there'd be no way to tick it back on.
    const [excludedProjectIds, setExcludedProjectIds] = useState<Set<number>>(new Set());
    const [excludedProfileIds, setExcludedProfileIds] = useState<Set<number>>(new Set());

    const toggle = (setter: React.Dispatch<React.SetStateAction<Set<number>>>) => (id: number) =>
        setter((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const toggleProjectExcluded = toggle(setExcludedProjectIds);
    const toggleProfileExcluded = toggle(setExcludedProfileIds);
    const [busy, setBusy] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const profileGrant: ProfileMembershipGrant | null = addToProfiles
        ? { isAdmin: profileRole === 'Admin' }
        : null;
    const projectGrant: ProjectMembershipGrant | null = addToProjects
        ? {
              isAdmin: projectRole === 'Admin',
              hasAnalytics: projectHasAnalytics,
              notificationsEnabled: projectNotificationsEnabled,
          }
        : null;

    const nothingSelected = !profileGrant && !projectGrant;

    // Reset form state on close. Deps [isOpen] only — form changes must not
    // re-run this.
    useEffect(() => {
        if (isOpen) return;
        setPreview(null);
        setLoadError(null);
        setAddToProfiles(true);
        setProfileRole('Admin');
        setAddToProjects(true);
        setProjectRole('Member');
        setProjectHasAnalytics(true);
        setProjectNotificationsEnabled(false);
    }, [isOpen]);

    // Edit-mode prefill: run ONCE per open (or when target user changes).
    // Cannot depend on form-field state — otherwise every checkbox toggle
    // would re-fetch the DTOs and overwrite what the user just changed.
    useEffect(() => {
        if (!isOpen || !isEdit) return;
        let cancelled = false;
        (async () => {
            try {
                const [projects, profiles] = await Promise.all([
                    getInheritedMemberProjects(profileId, userId),
                    getInheritedMemberProfiles(profileId, userId),
                ]);
                if (cancelled) return;
                setAddToProfiles(profiles.length > 0);
                if (profiles.length > 0) {
                    setProfileRole(profiles[0].isAdmin ? 'Admin' : 'Member');
                }
                setAddToProjects(projects.length > 0);
                if (projects.length > 0) {
                    setProjectRole(projects[0].isAdmin ? 'Admin' : 'Member');
                    setProjectHasAnalytics(!!projects[0].hasAnalytics);
                    setProjectNotificationsEnabled(!!projects[0].notificationsEnabled);
                }
            } catch (e: any) {
                if (!cancelled) setLoadError(e?.message || 'Failed to load current membership.');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen, isEdit, profileId, userId]);

    // Add-mode preview: refetches when the grant changes so the user can see
    // the impact of their choices before confirming. Only runs when NOT edit.
    useEffect(() => {
        if (!isOpen || isEdit) return;
        if (nothingSelected) {
            setPreview({ profiles: { willAdd: [], skipped: [] }, projects: { willAdd: [], skipped: [] } });
            return;
        }
        let cancelled = false;
        setPreview(null);
        (async () => {
            try {
                const p = await previewAddInheritedMember(profileId, userId, profileGrant, projectGrant);
                if (!cancelled) setPreview(p);
            } catch (e: any) {
                if (!cancelled) setLoadError(e?.message || 'Failed to load preview.');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen, isEdit, profileId, userId, addToProfiles, profileRole, addToProjects, projectRole, projectHasAnalytics, projectNotificationsEnabled]);

    const handleConfirm = async () => {
        setBusy(true);
        try {
            if (isEdit) {
                await editInheritedMember(profileId, userId, profileGrant, projectGrant);
            } else {
                await addInheritedMember(
                    profileId,
                    userId,
                    profileGrant,
                    projectGrant,
                    Array.from(excludedProjectIds),
                    Array.from(excludedProfileIds)
                );
            }
            onSuccess();
            onClose();
        } catch (e: any) {
            alert(e?.message || (isEdit ? 'Failed to update inherited member.' : 'Failed to add inherited member.'));
        } finally {
            setBusy(false);
        }
    };

    const willAddProfiles =
        preview?.profiles.willAdd.filter((p) => !excludedProfileIds.has(p.id)).length ?? 0;
    const skippedProfiles = preview?.profiles.skipped.length ?? 0;
    const willAddProjects =
        preview?.projects.willAdd.filter((p) => !excludedProjectIds.has(p.id)).length ?? 0;
    const skippedProjects = preview?.projects.skipped.length ?? 0;
    const willAddTotal = willAddProfiles + willAddProjects;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Dialog.Content contentWidth="md">
                <Dialog.Header>
                    {isEdit ? 'Edit' : 'Add'} {userDisplayName} globally under {profileName}
                </Dialog.Header>
                <div className={theme.body}>
                    <p className={theme.intro}>
                        Grants can be applied independently on the profile side and the project side. Any combination works.
                    </p>

                    <GrantSection
                        title="Descendant profiles"
                        enabled={addToProfiles}
                        onToggle={setAddToProfiles}>
                        <RoleRadio value={profileRole} onChange={setProfileRole} />
                    </GrantSection>

                    <GrantSection
                        title="Projects in this tree"
                        enabled={addToProjects}
                        onToggle={setAddToProjects}>
                        <RoleRadio value={projectRole} onChange={setProjectRole} />
                        <div className={theme.checkboxRowSpaced}>
                            <Checkbox
                                inputSize="sm"
                                checked={projectHasAnalytics}
                                onChange={(e) => setProjectHasAnalytics(e.target.checked)}>
                                Grant analytics access
                            </Checkbox>
                        </div>
                        <div className={theme.notificationsRow}>
                            <Checkbox
                                inputSize="sm"
                                checked={projectNotificationsEnabled}
                                onChange={(e) => setProjectNotificationsEnabled(e.target.checked)}>
                                Send all notifications
                            </Checkbox>
                            <div className={theme.notificationsHint}>
                                Off: they're only notified when it directly involves them — mentioned, assigned a case, or added to a task. On: notified like a normal project member (can be a lot for someone with access to every project).
                            </div>
                        </div>
                    </GrantSection>

                    {loadError ? (
                        <AlertBanner type="warning">{loadError}</AlertBanner>
                    ) : nothingSelected ? (
                        <AlertBanner type="info">Nothing selected — enable at least one grant.</AlertBanner>
                    ) : isEdit ? (
                        <AlertBanner type="info">
                            Applies new settings to every project and profile this user is already inherited on. Sticky exclusions and custom permissions are preserved.
                        </AlertBanner>
                    ) : !preview ? (
                        <LoadingIndicator />
                    ) : (
                        <>
                            <div className={theme.previewCounts}>
                                {addToProfiles && (
                                    <div className={theme.previewCountBox}>
                                        <div className={theme.previewCountLabel}>Profiles</div>
                                        <div className={theme.previewCountValue}>
                                            Will add: <strong>{willAddProfiles}</strong> · skip: {skippedProfiles}
                                        </div>
                                    </div>
                                )}
                                {addToProjects && (
                                    <div className={theme.previewCountBox}>
                                        <div className={theme.previewCountLabel}>Projects</div>
                                        <div className={theme.previewCountValue}>
                                            Will add: <strong>{willAddProjects}</strong> · skip: {skippedProjects}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {addToProfiles && (
                                <PreviewList
                                    title={`Profiles — ${willAddProfiles} will add`}
                                    items={preview.profiles.willAdd.map((p) => ({
                                        id: p.id,
                                        label: p.name,
                                        hint: p.isAdmin ? 'as Admin' : 'as Member',
                                    }))}
                                    isSelected={(id) => !excludedProfileIds.has(id)}
                                    onToggle={toggleProfileExcluded}
                                    onToggleAll={(selectAll) =>
                                        setExcludedProfileIds(
                                            selectAll
                                                ? new Set()
                                                : new Set(preview.profiles.willAdd.map((p) => p.id))
                                        )
                                    }
                                />
                            )}
                            {addToProfiles && preview.profiles.skipped.length > 0 && (
                                <PreviewList
                                    title={`Profiles — ${preview.profiles.skipped.length} skipped`}
                                    muted
                                    items={preview.profiles.skipped.map((p) => ({
                                        id: p.id,
                                        label: p.name,
                                        hint: labelForSkip(p.skipReason!),
                                    }))}
                                />
                            )}
                            {addToProjects && (
                                <PreviewList
                                    title={`Projects — ${willAddProjects} will add`}
                                    items={preview.projects.willAdd.map((p) => ({
                                        id: p.id,
                                        label: p.name,
                                        hint:
                                            (p.modules.length ? p.modules.join('·') : '—') +
                                            ' · ' +
                                            (p.isAdmin ? 'Admin' : 'Member') +
                                            (p.hasAnalytics ? ' + Analytics' : ''),
                                    }))}
                                    isSelected={(id) => !excludedProjectIds.has(id)}
                                    onToggle={toggleProjectExcluded}
                                    onToggleAll={(selectAll) =>
                                        setExcludedProjectIds(
                                            selectAll
                                                ? new Set()
                                                : new Set(preview.projects.willAdd.map((p) => p.id))
                                        )
                                    }
                                />
                            )}
                            {addToProjects && preview.projects.skipped.length > 0 && (
                                <PreviewList
                                    title={`Projects — ${preview.projects.skipped.length} skipped`}
                                    muted
                                    items={preview.projects.skipped.map((p) => ({
                                        id: p.id,
                                        label: p.name,
                                        hint: labelForSkip(p.skipReason!),
                                    }))}
                                />
                            )}
                        </>
                    )}
                </div>
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button onClick={onClose} variant="tertiary" disabled={busy}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            loading={busy}
                            onClick={handleConfirm}
                            disabled={nothingSelected || (!isEdit && (!preview || willAddTotal === 0))}>
                            {isEdit ? 'Update' : 'Confirm — add'}
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};

interface GrantSectionProps {
    title: string;
    enabled: boolean;
    onToggle(v: boolean): void;
    children: React.ReactNode;
}

const GrantSection: React.FC<GrantSectionProps> = ({ title, enabled, onToggle, children }) => (
    <div className={enabled ? theme.grantSection : `${theme.grantSection} ${theme.grantSectionDisabled}`}>
        <div className={theme.grantSectionHeader}>
            <Checkbox
                inputSize="sm"
                checked={enabled}
                onChange={(e) => onToggle(e.target.checked)}>
                <strong>{title}</strong>
            </Checkbox>
        </div>
        {enabled && <div className={theme.grantSectionBody}>{children}</div>}
    </div>
);

const RoleRadio: React.FC<{ value: 'Admin' | 'Member'; onChange(v: 'Admin' | 'Member'): void }> = ({ value, onChange }) => (
    <div className={theme.roleRadio}>
        {(['Admin', 'Member'] as const).map((r) => (
            <label
                key={r}
                className={value === r ? `${theme.roleOption} ${theme.roleOptionActive}` : theme.roleOption}>
                <input type="radio" checked={value === r} onChange={() => onChange(r)} />
                <strong>{r}</strong>
            </label>
        ))}
    </div>
);

const PreviewList: React.FC<{
    title: string;
    muted?: boolean;
    items: { id: number; label: string; hint: string }[];
    // When present, each row gets a checkbox. Absent = plain read-only list.
    isSelected?: (id: number) => boolean;
    onToggle?: (id: number) => void;
    onToggleAll?: (selectAll: boolean) => void;
}> = ({ title, muted, items, isSelected, onToggle, onToggleAll }) => {
    if (items.length === 0) return null;
    // No indeterminate state: a partial selection reads as unchecked, and the
    // "N will add" count in the title already says how many are picked.
    const allSelected = isSelected ? items.every((it) => isSelected(it.id)) : true;
    // Selectable lists open by default — a collapsed disclosure hides the
    // checkboxes entirely. The list body caps at 180px and scrolls.
    return (
        <details className={theme.previewDetails} open={!!onToggle || (!muted && items.length <= 6)}>
            <summary className={muted ? `${theme.previewSummary} ${theme.previewSummaryMuted}` : theme.previewSummary}>
                {title}
            </summary>
            <div className={muted ? `${theme.previewList} ${theme.previewListMuted}` : theme.previewList}>
                {onToggleAll && items.length > 1 && (
                    <div className={theme.previewRow}>
                        <span className={theme.previewRowLabel}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={() => onToggleAll(!allSelected)}
                                />{' '}
                                {allSelected ? 'Deselect all' : 'Select all'}
                            </label>
                        </span>
                    </div>
                )}
                {items.map((it) => (
                    <div key={it.id} className={muted ? `${theme.previewRow} ${theme.previewRowMuted}` : theme.previewRow}>
                        <span className={theme.previewRowLabel}>
                            {onToggle ? (
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={isSelected ? isSelected(it.id) : true}
                                        onChange={() => onToggle(it.id)}
                                    />{' '}
                                    {it.label}
                                </label>
                            ) : (
                                it.label
                            )}
                        </span>
                        <span className={theme.previewRowHint}>{it.hint}</span>
                    </div>
                ))}
            </div>
        </details>
    );
};

function labelForSkip(reason: string): string {
    switch (reason) {
        case 'already-member':
            return 'Already a member';
        case 'excluded-inherited':
            return 'Previously excluded';
        case 'previously-removed':
            return 'Previously removed';
        default:
            return reason;
    }
}

export default MakeWatcherConfirmModal;
